import cx from 'classnames';
import { t } from 'i18next';
import { Trans } from 'react-i18next';
import { Col } from '../grid/Col';
import { IRowProps, Row } from '../grid/Row';
import { TrackedLink } from '../link';
import PatronLevel from './patronLevel';
import { Stack } from '../stack/Stack';
import { FC } from 'react';
import {
  MOST_POPULAR_TIER,
  getPremiumTierPerks,
  resolvePerkValues,
  TPremiumTierPerk
} from '../subscriptions/premiumTierPerks';
import {
  buildPremiumAnalyticsItem,
  TPremiumCheckoutSurface,
  TPremiumRailPosition,
  trackPremiumPlanImpression
} from '../subscriptions/premiumXsollaAnalytics';
import { useTrackedImpression } from '../../scripts/hooks/useTrackedImpression';
import type { TSubscriptionsAppGame } from '../subscriptions/userSubscriptionsLinks';

type TDetailedPatreonPlugProps = {
  className?: string;
  premiumPage?: boolean;
  rowProps?: IRowProps;
  buttonTextVariants?: 'default' | 'unique';
  /**
   * When set, fires a one-shot view_item_list plan impression once the plug is
   * on screen, giving Patreon-fallback markets an impression→click funnel
   * comparable to the Xsolla rail. Left unset elsewhere (no impression fires).
   */
  analyticsSurface?: TPremiumCheckoutSurface;
  marketCountry?: string | null;
  railPosition?: TPremiumRailPosition;
};

type TPlugPanelProps = {
  patronLevelName: string;
  patronLevel: number;
  patronLevelClassName: string;
  monthlyAmount: number;
  monthlyUrl: string;
  annualUrl: string;
  buttonText: string;
  children?: any;
  className?: string;
  style?: any;
  showMostPopular?: boolean;
};

const PlugPanel: FC<TPlugPanelProps> = (props: TPlugPanelProps) => {
  return (
    <PatronLevel
      patronLevel={props.patronLevel}
      className={cx('rio-patreon-plug-panel h-full rio-shadow--large rio-pointer', props.className, {
        'rio-patreon-plug-panel--featured': props.showMostPopular
      })}
      contentClassName="h-full slds-grid slds-grid--vertical"
      onClick={(ev: any) => {
        ev.preventDefault();
        RIO_TRACK_EVENT('event', 'select_content', {
          content_type: 'clicked_patreon_plug',
          content_id: props.patronLevel
        });
        window.open(props.monthlyUrl, '_blank');
      }}
      style={{
        ...props.style,
        backgroundColor: 'rgba(0, 0, 0, 0.5)'
        // backgroundRepeat: 'repeat',
        // backgroundImage: `url(${asset('/images/bgtiles/stone.jpg')})`
      }}
    >
      {props.showMostPopular && (
        <div className="rio-patreon-plug-panel__ribbon">{t('patreon:most-popular')}</div>
      )}
      <Stack
        direction="vertical"
        spacingInRems={0}
        alignItems="center"
        justifyContent="center"
        className="slds-p-vertical--x-small rio-gutter slds-text-align--center"
      >
        <div
          className={cx(
            'slds-text-heading--medium  text-bold rio-text--title rio-title--caps',
            props.patronLevelClassName
          )}
        >
          {t('patreon:rank-patron', { rankName: props.patronLevelName })}
        </div>
        <div className="rio-subscription-panel__price">
          <span className="rio-subscription-panel__price-amount">
            {t('patreon:usd-amount', { amount: props.monthlyAmount })}
          </span>
          <span className="rio-subscription-panel__price-period">
            {t('xsolla:per-period', { period: t('common:terms.month') })}
          </span>
        </div>
      </Stack>
      <div className="slds-p-around--small slds-grid slds-grid--vertical slds-grow">
        <div className="slds-m-bottom--small">{props.children}</div>
        <TrackedLink
          href={props.monthlyUrl}
          onClick={(ev: any) => ev.preventDefault()}
          className={cx(
            'd-block slds-button text-bold slds-text-heading--medium slds-p-vertical--x-small',
            props.showMostPopular
              ? 'slds-button--premium-gradient rio-text-shadow--normal'
              : 'slds-button--brand-gold-outline'
          )}
          style={{ marginTop: 'auto' }}
        >
          {props.buttonText}
        </TrackedLink>

        <div className="slds-m-top--x-small slds-text-align--center">
          <TrackedLink
            href={props.annualUrl}
            target="_blank"
            onClick={(ev: any) => {
              // Keep the panel's monthly checkout onClick from hijacking this
              // click; the link itself opens the annual checkout in a new tab.
              ev.stopPropagation();
              RIO_TRACK_EVENT('event', 'select_content', {
                content_type: 'clicked_patreon_annual',
                content_id: props.patronLevel
              });
            }}
            className="rio-patreon-annual-link text-bold"
          >
            {t('patreon:save-with-annual')}
          </TrackedLink>
        </div>
      </div>
    </PatronLevel>
  );
};

type TPatronPlugPerksProps = {
  perks: TPremiumTierPerk[];
};

const PatronPlugPerks: FC<TPatronPlugPerksProps> = (props) => {
  return (
    <>
      {props.perks.map((perk) => {
        const { key, icon } = perk;
        const mainColSize = icon ? 11 : 12;
        return (
          <Row className="rio-patreon-plug-perk" verticalAlign="center" align="start" key={key}>
            {icon && (
              <Col size={1} className="slds-text-align--center slds-p-right--x-small">
                <i className={icon} />
              </Col>
            )}
            <Col size={mainColSize} className={icon ? 'slds-p-left--x-small' : ''}>
              <Trans
                i18nKey={key}
                values={resolvePerkValues(perk)}
                components={[<span className="text-raiderio text-bold" />]}
              />
            </Col>
          </Row>
        );
      })}
    </>
  );
};

export const DetailedPatreonPlug: FC<TDetailedPatreonPlugProps> = (props: TDetailedPatreonPlugProps) => {
  const game = RIO_SITE.game as TSubscriptionsAppGame;
  const { analyticsSurface, marketCountry, railPosition } = props;
  const impressionRef = useTrackedImpression<HTMLDivElement>({
    enabled: !!analyticsSurface,
    minVisibleMs: 900,
    onImpression: () => {
      if (!analyticsSurface) {
        return;
      }
      trackPremiumPlanImpression({
        checkoutSurface: analyticsSurface,
        marketCountry,
        railPosition,
        checkoutProvider: 'patreon',
        // Fixed USD tier pricing, mirroring the PlugPanel monthlyAmount props below.
        items: [
          buildPremiumAnalyticsItem({ tier: 'revered', billingPeriod: 'monthly', price: 5, currency: 'USD', index: 1 }),
          buildPremiumAnalyticsItem({
            tier: 'exalted',
            billingPeriod: 'monthly',
            price: 10,
            currency: 'USD',
            index: 2
          }),
          buildPremiumAnalyticsItem({
            tier: 'illustrious',
            billingPeriod: 'monthly',
            price: 20,
            currency: 'USD',
            index: 3
          })
        ]
      });
    }
  });

  return (
    <div className={props.className}>
      <div
        ref={impressionRef}
        style={{
          width: '100%',
          overflow: 'hidden'
        }}
      >
        <Row
          className="rio-patreon-plug"
          gutter={{
            xl: 'medium',
            lg: 'x-small',
            md: 'small',
            xs: 'x-small'
          }}
          verticalAlign={{
            md: 'stretch',
            xs: 'center'
          }}
          align={'center'}
          {...props.rowProps}
        >
          <Col lg={4} md={{ size: 6, order: 1 }} xs={{ size: 12, order: MOST_POPULAR_TIER === 'revered' ? 0 : 1 }}>
            <PlugPanel
              patronLevel={3}
              patronLevelName={t('patreon:ranks.revered')}
              patronLevelClassName="text-item-quality--4"
              monthlyAmount={5}
              buttonText={
                props.buttonTextVariants === 'unique'
                  ? t('patreon:go-type-for-x-usd', { type: t('patreon:ranks.revered').toUpperCase(), amount: 5 })
                  : t('patreon:join-for-x-usd', { amount: 5 })
              }
              monthlyUrl="https://www.patreon.com/checkout/RaiderIO/1828034"
              annualUrl="https://www.patreon.com/checkout/RaiderIO/1828034?cadence=12"
              showMostPopular={MOST_POPULAR_TIER === 'revered'}
            >
              <PatronPlugPerks perks={getPremiumTierPerks('revered', game)} />
            </PlugPanel>
          </Col>
          <Col lg={4} md={{ size: 6, order: 2 }} xs={{ size: 12, order: MOST_POPULAR_TIER === 'exalted' ? 0 : 2 }}>
            <PlugPanel
              patronLevel={4}
              patronLevelName={t('patreon:ranks.exalted')}
              patronLevelClassName="text-item-quality--5"
              monthlyAmount={10}
              buttonText={
                props.buttonTextVariants === 'unique'
                  ? t('patreon:get-type-for-x-usd', { type: t('patreon:ranks.exalted').toUpperCase(), amount: 10 })
                  : t('patreon:join-for-x-usd', { amount: 10 })
              }
              monthlyUrl="https://www.patreon.com/checkout/RaiderIO/3270036"
              annualUrl="https://www.patreon.com/checkout/RaiderIO/3270036?cadence=12"
              showMostPopular={MOST_POPULAR_TIER === 'exalted'}
            >
              <PatronPlugPerks perks={getPremiumTierPerks('exalted', game)} />
            </PlugPanel>
          </Col>
          <Col
            lg={4}
            md={{ size: 6, order: 3 }}
            xs={{ size: 12, order: MOST_POPULAR_TIER === 'illustrious' ? 0 : 3 }}
          >
            <PlugPanel
              patronLevel={5}
              patronLevelName={t('patreon:ranks.illustrious')}
              patronLevelClassName="text-item-quality--6"
              monthlyAmount={20}
              buttonText={
                props.buttonTextVariants === 'unique'
                  ? t('patreon:unlock-type-for-x-usd', {
                      type: t('patreon:ranks.illustrious').toUpperCase(),
                      amount: 20
                    })
                  : t('patreon:join-for-x-usd', { amount: 20 })
              }
              monthlyUrl="https://www.patreon.com/checkout/RaiderIO/9149916"
              annualUrl="https://www.patreon.com/checkout/RaiderIO/9149916?cadence=12"
              showMostPopular={MOST_POPULAR_TIER === 'illustrious'}
            >
              <PatronPlugPerks perks={getPremiumTierPerks('illustrious', game)} />
            </PlugPanel>
          </Col>
        </Row>
      </div>
      <div className="slds-text-align--center slds-text-body--small text-muted slds-m-top--medium">
        <i className="fa fa-rotate-left slds-m-right--xx-small" />
        {t('patreon:cancel-anytime-patreon')}
      </div>
    </div>
  );
};
